// Small pieces shared by the Elariom pages, in Elariom's own look (elariomTheme.js): serif
// section titles over a black rule, figures without cards, status as a dot and a word.

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';

// Local imports
import { STATUS } from './elariomData.js';
import { SERIF, INK, MUTED, dotted } from './elariomTheme.js';

export function SectionTitle({ title, blurb, right }) {
    return (
        <Box sx={{ borderBottom: `1px solid ${INK}`, paddingBottom: '16px' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                <Box>
                    <Typography component='h1' className='elariom-serif' sx={{ fontFamily: SERIF, fontSize: { xs: '30px', md: '36px' }, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em', color: INK, textWrap: 'balance' }}>{title}</Typography>
                    {blurb && <Typography sx={{ fontSize: '14px', color: MUTED, maxWidth: '640px', marginTop: '8px', lineHeight: 1.5 }}>{blurb}</Typography>}
                </Box>
                {right}
            </Box>
        </Box>
    );
}

// A smaller heading for a group inside a page, with the same rule.
export function GroupTitle({ title, blurb }) {
    return (
        <Box sx={{ borderBottom: `1px solid ${INK}`, paddingBottom: '10px', marginTop: '8px' }}>
            <Typography component='h2' className='elariom-serif' sx={{ fontFamily: SERIF, fontSize: '24px', fontWeight: 700, lineHeight: 1.15, color: INK }}>{title}</Typography>
            {blurb && <Typography sx={{ fontSize: '13px', color: MUTED, marginTop: '4px' }}>{blurb}</Typography>}
        </Box>
    );
}

// A figure in a row of figures: no card, separated from the next by a dotted line.
export function Stat({ value, label, hint }) {
    return (
        <Box sx={{ padding: '14px 18px 14px 0', borderRight: dotted, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
            <Typography className='elariom-serif' sx={{ fontFamily: SERIF, fontSize: '32px', fontWeight: 600, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', color: INK }}>{value}</Typography>
            <Typography sx={{ fontSize: '12px', color: INK }}>{label}</Typography>
            {hint && <Typography sx={{ fontSize: '11px', color: MUTED }}>{hint}</Typography>}
        </Box>
    );
}

export function StatRow({ children }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: '0 18px', borderBottom: dotted, '& > *:last-of-type': { borderRight: 'none' } }}>
            {children}
        </Box>
    );
}

// A small label. `dashed` marks something that does not exist yet.
export function Pill({ color, dashed, dot, children }) {
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '1px 8px', borderRadius: '999px', fontSize: '11px', whiteSpace: 'nowrap',
            color, background: dashed ? 'transparent' : alpha(color, 0.08), border: dashed ? `1px dashed ${alpha(color, 0.5)}` : `1px solid ${alpha(color, 0.18)}`,
        }}>
            {dot && <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', background: color }} />}
            {children}
        </Box>
    );
}

export function StatusPill({ status }) {
    const theme = useTheme();
    const s = STATUS[status] || STATUS.unknown;
    const color = {
        good: theme.palette.success.main,
        warn: theme.palette.warning.main,
        info: theme.palette.info.main,
    }[s.tone] || theme.palette.text.secondary;
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: s.tone === 'muted' || s.tone === 'planned' ? MUTED : color, whiteSpace: 'nowrap' }}>
            <Box sx={{ width: '7px', height: '7px', borderRadius: '50%', background: s.tone === 'planned' ? 'transparent' : color, border: s.tone === 'planned' ? `1px solid ${MUTED}` : 'none' }} />
            {s.label}
        </Box>
    );
}

// A row in a list, as in the reference: a small black-and-white icon, a name, a grey
// description, and one action on the right; rows separated by dotted lines.
export function ListRow({ icon, iconNode, title, titleAfter, subtitle, children, right, onClick, faded }) {
    const clickable = !!onClick;
    return (
        <Box
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={onClick}
            onKeyDown={clickable ? e => e.key === 'Enter' && onClick() : undefined}
            sx={{
                display: 'grid', gridTemplateColumns: '40px 1fr auto', alignItems: 'start', gap: '0 16px',
                padding: '18px 0', borderBottom: dotted, cursor: clickable ? 'pointer' : 'default',
                opacity: faded ? 0.6 : 1,
                '&:hover .elariom-row-title': clickable ? { textDecoration: 'underline', textUnderlineOffset: '3px' } : {},
                '&:focus-visible': { outline: `1px solid ${INK}`, outlineOffset: '4px' },
            }}
        >
            <Box sx={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: INK, fontSize: '22px' }}>
                {iconNode || icon}
            </Box>
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <Typography className='elariom-row-title' sx={{ fontSize: '15px', fontWeight: 600, color: INK }}>{title}</Typography>
                    {titleAfter}
                </Box>
                {subtitle && <Typography sx={{ fontSize: '13px', color: MUTED, lineHeight: 1.5 }}>{subtitle}</Typography>}
                {children}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '2px' }}>{right}</Box>
        </Box>
    );
}
